import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface SummaryMetric {
  score?: string;
  weight?: string;
  contribution?: string;
}

interface BaseCardData {
  summary: SummaryMetric;
  factors: Array<{ label: string; value: string }>;
}

interface CheckInBucketData {
  key: "fresh" | "aging" | "stale";
  label: string;
  devices?: number;
  ratio?: number;
  weight?: number;
  points?: number;
}

interface DeploymentCardsData {
  userLicenseRatio: BaseCardData;
  checkInScore: BaseCardData & {
    totalDevices?: number;
    buckets: CheckInBucketData[];
  };
  platformBreadthScore: BaseCardData & {
    platformActive?: number;
  };
}

const CARD_WIDTH = 360;

const CARD_COLORS = {
  user: { bg: "#EAF3FB", title: "#1252A3" },
  checkin: { bg: "#ECF6EA", title: "#1F7A36" },
  platform: { bg: "#F8F1E3", title: "#C15A00" }
};

const toText = (value: string | number): string => String(value ?? "").trim();

const parseNumber = (value: string | number | undefined): number | undefined => {
  if (value === undefined) return undefined;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : undefined;
};

const formatInt = (value: number | undefined): string => {
  if (value === undefined) return "-";
  return value.toLocaleString();
};

const formatDecimal = (value: number | undefined, digits = 2): string => {
  if (value === undefined) return "-";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits
  });
};

const formatCompactNumber = (value: number | undefined): string => {
  if (value === undefined) return "-";

  const abs = Math.abs(value);
  const formatScaled = (scaled: number): string => {
    if (Math.abs(scaled) >= 100) return Math.round(scaled).toString();
    return scaled.toFixed(1).replace(/\.0$/, "");
  };

  if (abs >= 1_000_000_000) return `${formatScaled(value / 1_000_000_000)}b`;
  if (abs >= 1_000_000) return `${formatScaled(value / 1_000_000)}m`;
  if (abs >= 1_000) return `${formatScaled(value / 1_000)}k`;
  return formatInt(value);
};

const formatRatioPercent = (value: number | undefined): string => {
  if (value === undefined) return "-";
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

const formatContributionPoints = (value: string | undefined): string => {
  if (!value || value.trim() === "") return "-";
  const trimmed = value.trim();
  if (/\bpts\b/i.test(trimmed)) return trimmed;
  const parsed = parseNumber(trimmed);
  if (parsed === undefined) return `${trimmed} pts`;
  return `${formatDecimal(parsed, 2)} pts`;
};

const normalizeSummaryKey = (metricName: string): keyof SummaryMetric | null => {
  const normalized = metricName.toLowerCase().trim();
  if (normalized === "score") return "score";
  if (normalized === "weight") return "weight";
  if (normalized === "weight percentage") return "weight";
  if (normalized === "contribution") return "contribution";
  if (normalized === "contribution points") return "contribution";
  return null;
};

const getBucketKey = (label: string): "fresh" | "aging" | "stale" | null => {
  const lower = label.toLowerCase();
  if (lower.includes("fresh") || lower.includes("<=28")) return "fresh";
  if (lower.includes("aging") || lower.includes("29-90")) return "aging";
  if (lower.includes("stale") || lower.includes("91+")) return "stale";
  return null;
};

const titleCaseSection = (sectionName: string): string => {
  if (sectionName === "UserLicenseRatio") return "User License Ratio";
  if (sectionName === "CheckInScore") return "Check-In Score";
  if (sectionName === "PlatformBreadthScore") return "Platform Breadth Score";
  return sectionName;
};

const normalizeCheckInLabel = (label: string): string => {
  return label
    .replace(/^Bucket\s*Checkin\s*/i, "")
    .replace(/\bDays\s+Days\b/gi, "Days")
    .replace(/\s+/g, " ")
    .trim();
};

const buildDeploymentCardsData = (rows: RawIndicatorRow[]): DeploymentCardsData => {
  const result: DeploymentCardsData = {
    userLicenseRatio: { summary: {}, factors: [] },
    checkInScore: {
      summary: {},
      factors: [],
      buckets: [
        { key: "fresh", label: "<= 28 Days (Fresh)" },
        { key: "aging", label: "29-90 Days (Aging)" },
        { key: "stale", label: "91+ Days (Stale)" }
      ]
    },
    platformBreadthScore: { summary: {}, factors: [] }
  };

  const bucketMap: Record<"fresh" | "aging" | "stale", CheckInBucketData> = {
    fresh: result.checkInScore.buckets[0],
    aging: result.checkInScore.buckets[1],
    stale: result.checkInScore.buckets[2]
  };

  rows.forEach((row) => {
    const section = (row.sectionName || "").trim();
    const metric = (row.metricName || "").trim();
    const label = (row.factorLabel || "").trim();
    const raw = toText(row.value);
    const summaryKey = normalizeSummaryKey(metric);

    if (section === "UserLicenseRatio") {
      if (summaryKey) {
        result.userLicenseRatio.summary[summaryKey] = summaryKey === "weight" && !raw.includes("%") ? `${raw}%` : raw;
      } else if (metric.toLowerCase() === "factors") {
        result.userLicenseRatio.factors.push({ label, value: raw });
      }
      return;
    }

    if (section === "CheckInScore") {
      if (summaryKey) {
        result.checkInScore.summary[summaryKey] = summaryKey === "weight" && !raw.includes("%") ? `${raw}%` : raw;
        return;
      }

      if (metric.toLowerCase() !== "factors") return;

      if (label.toLowerCase() === "total devices") {
        result.checkInScore.totalDevices = parseNumber(raw);
        return;
      }

      const bucketKey = getBucketKey(label);
      if (!bucketKey) {
        result.checkInScore.factors.push({ label, value: raw });
        return;
      }

      const bucket = bucketMap[bucketKey];
      if (label.toLowerCase().startsWith("bucket checkin")) {
        bucket.label = normalizeCheckInLabel(raw);
      } else if (label.toLowerCase().startsWith("devices checkin")) {
        bucket.devices = parseNumber(raw);
      } else if (label.toLowerCase().startsWith("ratio checkin")) {
        bucket.ratio = parseNumber(raw);
      } else if (label.toLowerCase().startsWith("weight checkin")) {
        bucket.weight = parseNumber(raw);
      } else if (label.toLowerCase().startsWith("percent checkin")) {
        bucket.points = parseNumber(raw);
      }
      return;
    }

    if (section === "PlatformBreadthScore") {
      if (summaryKey) {
        result.platformBreadthScore.summary[summaryKey] = summaryKey === "weight" && !raw.includes("%") ? `${raw}%` : raw;
      } else if (metric.toLowerCase() === "factors") {
        if (label.toLowerCase() === "platform active") {
          result.platformBreadthScore.platformActive = parseNumber(raw);
        } else {
          result.platformBreadthScore.factors.push({ label, value: raw });
        }
      }
    }
  });

  return result;
};

const SummaryRow = ({ summary, scoreColor }: { summary: SummaryMetric; scoreColor: string }) => {
  return (
    <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: "100%" } }}>
      <Stack styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Score</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: scoreColor } }}>{summary.score || "-"}</Text>
      </Stack>
      <Stack styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Weight</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700 } }}>{summary.weight || "-"}</Text>
      </Stack>
      <Stack styles={{ root: { flex: 1 } }}>
        <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Contribution</Text>
        <Text variant="xLarge" styles={{ root: { fontWeight: 700 } }}>{formatContributionPoints(summary.contribution)}</Text>
      </Stack>
    </Stack>
  );
};

const UserLicenseRatioCard = ({ data }: { data: BaseCardData }) => {
  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          width: CARD_WIDTH,
          minWidth: 320,
          borderRadius: 8,
          padding: 16,
          border: "1px solid #D9D9D9",
          backgroundColor: CARD_COLORS.user.bg
        }
      }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: CARD_COLORS.user.title } }}>
        {titleCaseSection("UserLicenseRatio")}
      </Text>

      <SummaryRow summary={data.summary} scoreColor={CARD_COLORS.user.title} />

      <div style={{ height: 1, backgroundColor: "#C9DBEE" }} />

      <Text variant="medium" styles={{ root: { fontWeight: 600, color: CARD_COLORS.user.title } }}>Factors</Text>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
        {[...data.factors]
          .sort((a, b) => {
            const aIsRatio = a.label.toLowerCase().includes("ratio");
            const bIsRatio = b.label.toLowerCase().includes("ratio");
            if (aIsRatio && !bIsRatio) return 1;
            if (!aIsRatio && bIsRatio) return -1;
            return 0;
          })
          .map((factor) => (
          <Stack
            key={factor.label}
            styles={{
              root: {
                minHeight: 86,
                border: "1px solid #D9D9D9",
                borderRadius: 6,
                backgroundColor: "#FFFFFF",
                padding: "8px 10px",
                alignItems: "center",
                justifyContent: "space-between"
              }
            }}
          >
            <Text variant="small" styles={{ root: { textAlign: "center" } }}>{factor.label}</Text>
            <Text variant="large" styles={{ root: { fontWeight: 700 } }}>{factor.value}</Text>
          </Stack>
        ))}
      </div>
    </Stack>
  );
};

const CheckInScoreCard = ({ data }: { data: DeploymentCardsData["checkInScore"] }) => {
  const totalDevices = data.totalDevices ?? data.buckets.reduce((sum, item) => sum + (item.devices || 0), 0);

  const barParts = data.buckets.map((bucket) => {
    const devices = bucket.devices || 0;
    const widthPct = totalDevices > 0 ? (devices / totalDevices) * 100 : 0;
    const ratioText = bucket.ratio !== undefined ? `${formatDecimal(bucket.ratio, 2)}%` : "-";
    const ratioShortText = bucket.ratio !== undefined ? `${formatDecimal(bucket.ratio, 1)}%` : "-";
    return {
      ...bucket,
      widthPct,
      ratioText,
      ratioShortText,
      devicesCompactText: formatCompactNumber(bucket.devices),
      devicesText: formatInt(bucket.devices)
    };
  });

  const pointsTotal = data.buckets.reduce((sum, bucket) => sum + (bucket.points || 0), 0);

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          width: CARD_WIDTH,
          minWidth: 320,
          borderRadius: 8,
          padding: 16,
          border: "1px solid #D9D9D9",
          backgroundColor: CARD_COLORS.checkin.bg
        }
      }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: CARD_COLORS.checkin.title } }}>
        {titleCaseSection("CheckInScore")}
      </Text>

      <SummaryRow summary={data.summary} scoreColor={CARD_COLORS.checkin.title} />

      <div style={{ height: 1, backgroundColor: "#C7DECC" }} />

      <Text variant="medium" styles={{ root: { fontWeight: 600, color: CARD_COLORS.checkin.title } }}>
        Factors
      </Text>

      <div
        style={{
          display: "flex",
          width: "100%",
          height: 28,
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid #D9D9D9",
          backgroundColor: "#FFFFFF"
        }}
      >
        {barParts.map((part, index) => {
          const colors = ["#50B14F", "#F2A100", "#E74C3C"];
          const width = `${Math.max(part.widthPct, 0)}%`;
          return (
            <div
              key={`${part.key}-${index}`}
              style={{
                width,
                minWidth: part.widthPct > 0 ? 28 : 0,
                backgroundColor: colors[index],
                color: "#FFFFFF",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
              title={`${part.devicesText} (${part.ratioText})`}
            >
              {part.widthPct > 20
                ? `${part.devicesCompactText} (${part.ratioShortText})`
                : part.widthPct > 8
                  ? part.devicesCompactText
                  : ""}
            </div>
          );
        })}
      </div>

      <div style={{ border: "1px solid #D9D9D9", borderRadius: 6, backgroundColor: "#FFFFFF", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1fr", padding: "8px 10px", fontWeight: 700, borderBottom: "1px solid #EDEBE9", backgroundColor: "#0078D4" }}>
          <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 700 } }}>Bucket</Text>
          <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 700 } }}>Devices</Text>
          <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 700 } }}>Ratio</Text>
          <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 700 } }}>Weight</Text>
          <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 700 } }}>Points</Text>
        </div>
        {data.buckets.map((bucket) => (
          <div key={bucket.key} style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1fr", padding: "8px 10px", borderBottom: "1px solid #EDEBE9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: bucket.key === "fresh" ? "#50B14F" : bucket.key === "aging" ? "#F2A100" : "#E74C3C",
                  display: "inline-block",
                  flexShrink: 0
                }}
              />
              <Text variant="small">{bucket.label}</Text>
            </div>
            <Text variant="small">{formatInt(bucket.devices)}</Text>
            <Text variant="small">{formatRatioPercent(bucket.ratio)}</Text>
            <Text variant="small">{formatDecimal(bucket.weight, 2)}</Text>
            <Text variant="small">{formatDecimal(bucket.points, 2)}</Text>
          </div>
        ))}
      </div>

      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
          {formatInt(totalDevices)} total managed devices
        </Text>
        <Text variant="medium" styles={{ root: { textAlign: "right", color: "#0B6A0B", fontWeight: 700 } }}>
          Sum = {formatDecimal(pointsTotal, 2)}
        </Text>
      </Stack>
    </Stack>
  );
};

const toBool = (value: string): boolean | null => {
  const normalized = value.toLowerCase();
  if (["yes", "true", "1"].includes(normalized)) return true;
  if (["no", "false", "0"].includes(normalized)) return false;
  return null;
};

const PlatformBreadthScoreCard = ({ data }: { data: DeploymentCardsData["platformBreadthScore"] }) => {
  const activePlatforms = data.platformActive ?? data.factors.filter((factor) => toBool(factor.value) === true).length;

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          width: CARD_WIDTH,
          minWidth: 320,
          borderRadius: 8,
          padding: 16,
          border: "1px solid #D9D9D9",
          backgroundColor: CARD_COLORS.platform.bg
        }
      }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 700, color: CARD_COLORS.platform.title } }}>
        {titleCaseSection("PlatformBreadthScore")}
      </Text>

      <SummaryRow summary={data.summary} scoreColor={CARD_COLORS.platform.title} />

      <div style={{ height: 1, backgroundColor: "#E2CFAD" }} />

      <Text variant="medium" styles={{ root: { fontWeight: 600, color: CARD_COLORS.platform.title } }}>Factors</Text>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
        {data.factors.map((factor) => {
          const boolValue = toBool(factor.value);
          const isTrue = boolValue === true;
          const isFalse = boolValue === false;
          return (
            <Stack
              key={factor.label}
              styles={{
                root: {
                  minHeight: 78,
                  border: "1px solid #D9D9D9",
                  borderRadius: 6,
                  backgroundColor: "#FFFFFF",
                  padding: "8px 10px",
                  alignItems: "center",
                  justifyContent: "space-between"
                }
              }}
            >
              <Text variant="small" styles={{ root: { textAlign: "center" } }}>{factor.label}</Text>
              {isTrue || isFalse ? (
                <Icon
                  iconName={isTrue ? "CheckMark" : "Cancel"}
                  styles={{ root: { color: isTrue ? "#107C10" : "#A4262C", fontSize: 18 } }}
                />
              ) : (
                <Text variant="large" styles={{ root: { fontWeight: 700 } }}>{factor.value}</Text>
              )}
            </Stack>
          );
        })}
      </div>

      <Text variant="medium" styles={{ root: { textAlign: "center", fontWeight: 600 } }}>
        {activePlatforms} platforms active {"->"} Score: {data.summary.score || "-"}
      </Text>
    </Stack>
  );
};

interface IntuneDeploymentRawIndicatorCardsProps {
  rows: RawIndicatorRow[];
}

const IntuneDeploymentRawIndicatorCards: React.FC<IntuneDeploymentRawIndicatorCardsProps> = ({ rows }) => {
  const cardsData = useMemo(() => buildDeploymentCardsData(rows), [rows]);

  return (
    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
      <UserLicenseRatioCard data={cardsData.userLicenseRatio} />
      <CheckInScoreCard data={cardsData.checkInScore} />
      <PlatformBreadthScoreCard data={cardsData.platformBreadthScore} />
    </Stack>
  );
};

export default IntuneDeploymentRawIndicatorCards;